// WebGPU + memory feature detection. Pure runtime check — no module-load-time
// side effects, so tests in Node/happy-dom (where navigator.gpu is undefined)
// return { available: false, reason: ... } cleanly.

export interface FeatureCheck {
  available: boolean;
  /** Human-readable reason, shown to the editor. */
  reason: string;
  /** Best-guess GPU adapter info when present. */
  adapterInfo?: { vendor: string; architecture: string };
}

interface GPUAdapter {
  info?: { vendor?: string; architecture?: string };
  requestAdapterInfo?: () => Promise<{ vendor?: string; architecture?: string }>;
}

interface GPU {
  requestAdapter: () => Promise<GPUAdapter | null>;
}

function getGpu(): GPU | null {
  if (typeof navigator === 'undefined') return null;
  const nav = navigator as unknown as { gpu?: GPU };
  return nav.gpu ?? null;
}

export async function checkWebGpu(): Promise<FeatureCheck> {
  const gpu = getGpu();
  if (!gpu) {
    return {
      available: false,
      reason: 'WebGPU is unavailable in this browser. Update Chrome to 113+ or check chrome://flags/#enable-unsafe-webgpu.',
    };
  }
  try {
    const adapter = await gpu.requestAdapter();
    if (!adapter) {
      return {
        available: false,
        reason: 'WebGPU adapter could not be requested. Your GPU driver may not support it.',
      };
    }
    let info: { vendor?: string; architecture?: string } | undefined = adapter.info;
    if (!info && adapter.requestAdapterInfo) {
      try {
        info = await adapter.requestAdapterInfo();
      } catch {
        info = undefined;
      }
    }
    return {
      available: true,
      reason: 'WebGPU available.',
      adapterInfo: info
        ? { vendor: info.vendor ?? 'unknown', architecture: info.architecture ?? 'unknown' }
        : undefined,
    };
  } catch (err) {
    return {
      available: false,
      reason: err instanceof Error ? err.message : 'WebGPU adapter request failed.',
    };
  }
}
